// v1.28.66 — Payment-Mode-wise report with row drill-down + reassign.
//
// Bug fixes this version addresses:
//   1. Missing modes → backend now includes payouts, so modes used only on
//      money-out entries (e.g. GOODLUCK WITHDRAW) appear.
//   2. Drill-down → each mode row is now clickable and opens a full-page
//      table of every individual transaction backing that mode.
//   3. Unspecified rows → the drill-down for "Unspecified" gains a bulk
//      "Reassign to…" button that rewrites payment_mode on every entry in
//      the range in one call.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { dmY } from "@/lib/dateFmt";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    CurrencyInr, ArrowLeft, ArrowsCounterClockwise, DownloadSimple, Wallet, ChartPie,
    CaretRight, Warning, Swap, MagnifyingGlass,
} from "@phosphor-icons/react";
import { withDatePrefix } from "@/lib/downloadFilename";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmt2 = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const MODE_COLORS = [
    "from-emerald-500 to-teal-600",
    "from-blue-500 to-indigo-600",
    "from-amber-500 to-orange-500",
    "from-fuchsia-500 to-pink-600",
    "from-cyan-500 to-sky-600",
    "from-violet-500 to-purple-600",
    "from-rose-500 to-red-600",
    "from-lime-500 to-green-600",
];

export default function PaymentModeReport() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [data, setData] = useState(null);
    const [drill, setDrill] = useState(null); // { mode, rows, totals, loading }
    const [reassignOpen, setReassignOpen] = useState(false);
    const [reassignTo, setReassignTo] = useState("");
    const [modes, setModes] = useState([]);
    // v1.28.69 — Direction filter chip: "all" | "in" | "out"
    const [directionFilter, setDirectionFilter] = useState("all");
    // v1.28.77 — Drill-down filters
    const [drillDirection, setDrillDirection] = useState("all");
    const [drillParty, setDrillParty] = useState("");
    const [drillSearch, setDrillSearch] = useState("");

    const load = async (fromDate = dateFrom, toDate = dateTo) => {
        setLoading(true);
        try {
            const params = {};
            if (fromDate) params.date_from = fromDate;
            if (toDate) params.date_to = toDate;
            const { data } = await api.get("/reports/payment-modes", { params });
            setData(data);
            if (!fromDate && data.date_from) setDateFrom(data.date_from);
            if (!toDate && data.date_to) setDateTo(data.date_to);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); api.get("/payment-modes").then(r => setModes(r.data || [])).catch(() => {}); /* eslint-disable-next-line */ }, []);

    const openDrill = async (mode) => {
        setDrill({ mode, rows: null, totals: null, loading: true });
        try {
            const params = { mode };
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const { data } = await api.get("/reports/payment-modes/details", { params });
            setDrill({ mode, rows: data.rows || [], totals: data.totals || { count: 0, amount: 0 }, loading: false });
        } catch (e) { toast.error(formatApiError(e)); setDrill(null); }
    };

    const closeDrill = () => {
        setDrill(null); setReassignOpen(false); setReassignTo("");
        setDrillDirection("all"); setDrillParty(""); setDrillSearch("");
    };

    // v1.28.77 — Excel export for the drill-down view. Respects every
    // active filter (direction / party / remarks search / date range).
    const downloadDrillXlsx = async () => {
        if (!drill?.mode) return;
        try {
            const params = { mode: drill.mode };
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (drillDirection !== "all") params.direction = drillDirection;
            if (drillParty) params.party_id = drillParty;
            if (drillSearch.trim()) params.search = drillSearch.trim();
            const r = await api.get("/reports/payment-modes/details.xlsx", {
                params, responseType: "blob",
            });
            const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safe = (drill.mode || "Mode").replace(/[^A-Za-z0-9]+/g, "");
            a.download = withDatePrefix(`PaymentMode_${safe}.xlsx`);
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const applyPreset = (months) => {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
        const f = from.toISOString().slice(0, 10);
        const t = now.toISOString().slice(0, 10);
        setDateFrom(f); setDateTo(t);
        load(f, t);
    };

    const runReassign = async () => {
        const to = (reassignTo || "").trim();
        if (!to) { toast.error("Pick a target payment mode"); return; }
        if (!drill) return;
        if (!window.confirm(`Reassign ${drill.totals?.count || 0} entries from "${drill.mode}" to "${to}"?`)) return;
        try {
            const body = { to_mode: to, from_mode: drill.mode };
            if (dateFrom) body.date_from = dateFrom;
            if (dateTo) body.date_to = dateTo;
            const { data } = await api.post("/reports/payment-modes/reassign", body);
            toast.success(`Reassigned ${data.modified} entries → ${to}`);
            setReassignOpen(false); setReassignTo("");
            await load(); await openDrill(to);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const rows = data?.rows || [];
    const totals = data?.totals || { count: 0, amount: 0, modes: 0 };
    const maxAmt = useMemo(() => Math.max(1, ...rows.map((r) => r.amount || 0)), [rows]);

    // v1.28.69 — Direction mis-tag detector. Flags modes whose name suggests
    // one direction (Receipt / Payout) but whose entries include the opposite
    // direction, so admins can spot mis-categorised transactions quickly.
    const looksLikeReceipt = (name) => /(recd|received|receipt|credit|deposit|inward|collection|\bin\b)/i.test((name || ""));
    const looksLikePayout  = (name) => /(paid|payout|withdraw|debit|outward|expense|\bout\b)/i.test((name || ""));
    const mismatches = useMemo(() => rows.filter((r) => {
        if (!r.mode) return false;
        if (looksLikeReceipt(r.mode) && (r.count_out || 0) > 0) return true;
        if (looksLikePayout(r.mode)  && (r.count_in  || 0) > 0) return true;
        return false;
    }), [rows]);

    // v1.28.69 — Direction filter applied to the main list + detailed table.
    const filteredRows = useMemo(() => {
        if (directionFilter === "in")  return rows.filter((r) => (r.amount_in  || 0) > 0);
        if (directionFilter === "out") return rows.filter((r) => (r.amount_out || 0) > 0);
        return rows;
    }, [rows, directionFilter]);

    const downloadXlsx = async () => {
        const qs = new URLSearchParams();
        if (dateFrom) qs.set("date_from", dateFrom);
        if (dateTo) qs.set("date_to", dateTo);
        try {
            const res = await api.get(`/reports/payment-modes.xlsx?${qs.toString()}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = withDatePrefix(`payment-modes-${dateFrom || "all"}-to-${dateTo || "now"}.xlsx`);
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // ── Drill-down view ─────────────────────────────────────────────────
    if (drill) {
        const isUnspecified = (drill.mode || "").toLowerCase() === "unspecified";
        const canReassign = user?.role === "admin";
        // v1.28.77 — Party list = distinct parties present in this drill.
        const partyOptions = (() => {
            const seen = new Map();
            (drill.rows || []).forEach((r) => {
                if (r.party_id && !seen.has(r.party_id)) seen.set(r.party_id, r.party_name || "—");
            });
            return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
        })();
        const q = drillSearch.trim().toLowerCase();
        const passRow = (r) => {
            if (drillParty && r.party_id !== drillParty) return false;
            if (q && !(r.remarks || "").toLowerCase().includes(q)) return false;
            return true;
        };
        const allIn  = (drill.rows || []).filter((r) => r.txn_type !== "payout").filter(passRow);
        const allOut = (drill.rows || []).filter((r) => r.txn_type === "payout").filter(passRow);
        const rowsIn  = drillDirection === "out" ? [] : allIn;
        const rowsOut = drillDirection === "in"  ? [] : allOut;
        const sumIn  = rowsIn.reduce((s, r) => s + Number(r.amount || 0), 0);
        const sumOut = rowsOut.reduce((s, r) => s + Number(r.amount || 0), 0);
        return (
            <div className="space-y-4" data-testid="payment-mode-drill">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <button onClick={closeDrill} data-testid="pm-drill-back"
                                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                            <ArrowLeft size={12} /> Back to Payment Modes Report
                        </button>
                        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">
                            {drill.mode}
                        </h1>
                        <p className="mt-1 text-sm text-slate-600">
                            {drill.loading ? "Loading…" : (
                                <>Received {fmt2(sumIn)} · Paid {fmt2(sumOut)} · Net {fmt2(sumIn - sumOut)}</>
                            )}
                            {dateFrom && dateTo && ` · ${dmY(dateFrom)} → ${dmY(dateTo)}`}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {isUnspecified && canReassign && !drill.loading && (drill.totals?.count || 0) > 0 && (
                            <Button type="button" onClick={() => setReassignOpen(true)}
                                    data-testid="pm-drill-reassign-btn"
                                    className="h-9 bg-amber-600 text-white hover:bg-amber-700">
                                <Swap size={13} className="mr-1" weight="bold" /> Reassign Payment Mode
                            </Button>
                        )}
                        <Button type="button" onClick={downloadDrillXlsx}
                                data-testid="pm-drill-download-xlsx"
                                variant="outline"
                                className="h-9 border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50">
                            <DownloadSimple size={13} className="mr-1" /> Download Excel
                        </Button>
                    </div>
                </div>

                {/* v1.28.77 — Drill-down filter bar */}
                <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white px-3 py-3 shadow-sm" data-testid="pm-drill-filters">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Show</Label>
                        <select
                            data-testid="pm-drill-direction"
                            value={drillDirection}
                            onChange={(e) => setDrillDirection(e.target.value)}
                            className="mt-1 h-9 rounded-md border border-slate-300 bg-white px-2 text-sm outline-none focus:border-blue-400"
                        >
                            <option value="all">All</option>
                            <option value="in">Received (IN) only</option>
                            <option value="out">Paid (OUT) only</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Party</Label>
                        <select
                            data-testid="pm-drill-party"
                            value={drillParty}
                            onChange={(e) => setDrillParty(e.target.value)}
                            className="mt-1 h-9 min-w-[180px] rounded-md border border-slate-300 bg-white px-2 text-sm outline-none focus:border-blue-400"
                        >
                            <option value="">All Parties ({partyOptions.length})</option>
                            {partyOptions.map(([pid, pname]) => (
                                <option key={pid} value={pid}>{pname}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-1 min-w-[220px]">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">Search remarks</Label>
                        <div className="mt-1 flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-2">
                            <MagnifyingGlass size={14} className="text-slate-400" />
                            <input
                                type="text"
                                data-testid="pm-drill-search"
                                placeholder="e.g. Goodluck"
                                value={drillSearch}
                                onChange={(e) => setDrillSearch(e.target.value)}
                                className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
                            />
                            {drillSearch && (
                                <button type="button" onClick={() => setDrillSearch("")}
                                        data-testid="pm-drill-search-clear"
                                        className="text-[11px] text-slate-500 underline hover:text-rose-600">clear</button>
                            )}
                        </div>
                    </div>
                    {(drillDirection !== "all" || drillParty || drillSearch) && (
                        <button type="button"
                                data-testid="pm-drill-filters-reset"
                                onClick={() => { setDrillDirection("all"); setDrillParty(""); setDrillSearch(""); }}
                                className="h-9 self-end rounded-md border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                            Reset
                        </button>
                    )}
                </div>

                {[{ title: "Received (IN)", rows: rowsIn, sum: sumIn, tint: "emerald", sign: "+", testId: "pm-drill-in" },
                  { title: "Paid (OUT)",    rows: rowsOut, sum: sumOut, tint: "rose",    sign: "−", testId: "pm-drill-out" }].filter((sec) => sec.rows.length > 0 || drill.loading || (drillDirection === "all" && !drillParty && !drillSearch)).map((sec) => (
                    <section key={sec.title} className="rounded-lg border border-slate-200 bg-white shadow-sm">
                        <div className={`flex items-center justify-between border-b border-slate-100 px-4 py-2 text-${sec.tint}-800`}>
                            <div className="font-display text-sm font-semibold uppercase tracking-wider">{sec.title}</div>
                            <div className="text-xs text-slate-500">
                                {sec.rows.length} entr{sec.rows.length === 1 ? "y" : "ies"} · <span className={`font-mono font-bold text-${sec.tint}-700`}>{sec.sign}{fmt2(sec.sum)}</span>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm" data-testid={sec.testId}>
                                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                    <tr>
                                        <th className="px-4 py-2 text-left">Date</th>
                                        <th className="px-4 py-2 text-left">Party</th>
                                        <th className="px-4 py-2 text-left">UTR / Ref</th>
                                        <th className="px-3 py-2 text-right">Amount</th>
                                        <th className="px-3 py-2 text-left">Remarks</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {sec.rows.length === 0 && !drill.loading && (
                                        <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No entries.</td></tr>
                                    )}
                                    {sec.rows.map((r) => (
                                        <tr key={r.id} className="hover:bg-slate-50">
                                            <td className="px-4 py-2 whitespace-nowrap text-slate-800">{dmY(r.date)}</td>
                                            <td className="px-4 py-2 text-slate-900">
                                                {r.party_name ? (
                                                    <Link to={`/admin/transactions?party_id=${r.party_id}`} data-testid={`pm-drill-party-link-${r.party_id}`} className="hover:underline">{r.party_name}</Link>
                                                ) : <em className="text-slate-400">—</em>}
                                            </td>
                                            <td className="px-4 py-2 font-mono text-xs text-slate-700">{r.utr || "—"}</td>
                                            <td className={`px-3 py-2 text-right font-mono tabular-nums font-semibold text-${sec.tint}-700`}>
                                                {sec.sign}{fmt2(r.amount)}
                                            </td>
                                            <td className="px-4 py-2 text-slate-600">{r.remarks || ""}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                ))}

                {reassignOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
                         data-testid="pm-reassign-modal" role="dialog" aria-modal="true">
                        <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                            <div className="border-b border-slate-100 px-5 py-3 text-sm font-bold uppercase tracking-wider text-slate-700">
                                Reassign {drill.totals?.count || 0} entries
                            </div>
                            <div className="space-y-3 px-5 py-4">
                                <div>
                                    <Label className="text-xs font-semibold text-slate-600">New Payment Mode</Label>
                                    <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}
                                            data-testid="pm-reassign-target"
                                            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-400">
                                        <option value="">— pick a mode —</option>
                                        {modes.map((m) => (
                                            <option key={m.id || m.name} value={m.name}>{m.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="text-[11px] text-slate-500">
                                    Only applies to entries currently marked <b>{drill.mode}</b> and dated between {dmY(dateFrom)} → {dmY(dateTo)}.
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
                                <button type="button" onClick={() => setReassignOpen(false)}
                                        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                                <button type="button" onClick={runReassign}
                                        data-testid="pm-reassign-confirm"
                                        className="rounded-md bg-amber-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-amber-700">
                                    Reassign
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ── Main report ─────────────────────────────────────────────────────
    return (
        <div className="space-y-6" data-testid="payment-mode-report-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={12} /> Back to Dashboard
                    </Link>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">Payment Modes Report</h1>
                    <p className="mt-1 text-sm text-slate-600">Every recorded payment & payout split by mode · click a row to drill down.</p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">From</Label>
                        <Input type="date" data-testid="pm-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 h-9 text-sm" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500">To</Label>
                        <Input type="date" data-testid="pm-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 h-9 text-sm" />
                    </div>
                    <Button type="button" onClick={() => load()} disabled={loading} data-testid="pm-apply" className="h-9 bg-blue-600 text-white hover:bg-blue-700">
                        <ArrowsCounterClockwise size={13} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Apply
                    </Button>
                    <Button type="button" onClick={downloadXlsx} data-testid="pm-download-xlsx" variant="outline" className="h-9 border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50">
                        <DownloadSimple size={13} className="mr-1" /> Download Excel
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                {[1, 3, 6, 12].map((m) => (
                    <button key={m} type="button" onClick={() => applyPreset(m)} data-testid={`pm-preset-${m}m`}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700">
                        Last {m} {m === 1 ? "month" : "months"}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Kpi label="Total Received" value={fmt(totals.amount_in)} icon={CurrencyInr} tone="emerald" testId="kpi-pm-in" accent />
                <Kpi label="Total Paid" value={fmt(totals.amount_out)} icon={CurrencyInr} tone="rose" testId="kpi-pm-out" />
                <Kpi label="Net" value={fmt(totals.net)} icon={Wallet} tone={Number(totals.net) < 0 ? "rose" : "emerald"} testId="kpi-pm-net" />
                <Kpi label="Modes Used" value={String(totals.modes || 0)} icon={ChartPie} tone="amber" testId="kpi-pm-modes" />
            </div>

            {/* v1.28.69 — Direction filter chips */}
            <div className="flex flex-wrap items-center gap-2" data-testid="pm-direction-filter">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">Show:</span>
                {[
                    { id: "all", label: "All", cls: "bg-slate-900 text-white" },
                    { id: "in",  label: "Received only", cls: "bg-emerald-600 text-white" },
                    { id: "out", label: "Paid only", cls: "bg-rose-600 text-white" },
                ].map((f) => {
                    const active = directionFilter === f.id;
                    return (
                        <button key={f.id} type="button"
                                data-testid={`pm-direction-${f.id}`}
                                onClick={() => setDirectionFilter(f.id)}
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${active ? f.cls : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                            {f.label}
                        </button>
                    );
                })}
            </div>

            {/* v1.28.69 — Mismatch alert */}
            {mismatches.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                     data-testid="pm-mismatch-alert">
                    <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-amber-700" />
                    <div>
                        <div className="font-semibold">Possible direction mis-tag on {mismatches.length} mode{mismatches.length === 1 ? "" : "s"}.</div>
                        <div className="text-xs text-amber-900/90">
                            {mismatches.map((m, i) => (
                                <span key={m.mode}>
                                    {i > 0 && " · "}
                                    <button type="button" className="underline underline-offset-2 font-semibold hover:text-amber-950"
                                            onClick={() => openDrill(m.mode)}
                                            data-testid={`pm-mismatch-${m.mode.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {m.mode}
                                    </button>
                                    {" "}
                                    <span className="opacity-70">
                                        ({looksLikeReceipt(m.mode) ? `${m.count_out} paid` : `${m.count_in} received`})
                                    </span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                    <div className="font-display text-sm font-semibold text-slate-900">By Payment Mode · <span className="text-slate-500">click to drill down · <span className="text-emerald-700 font-bold">green = received</span> · <span className="text-rose-700 font-bold">red = paid</span></span></div>
                    <div className="text-[11px] text-slate-500">{dmY(dateFrom)} — {dmY(dateTo)}</div>
                </div>
                {rows.length === 0 ? (
                    <div className="p-6"><EmptyState icon={ChartPie} title="No collections in this range" description="Try widening the date range or select an earlier preset." /></div>
                ) : (
                    <ul className="space-y-2" data-testid="pm-mode-list">
                        {filteredRows.map((r) => {
                            const gross = (r.amount_in || 0) + (r.amount_out || 0);
                            const inPct = gross ? Math.round((r.amount_in / gross) * 100) : 0;
                            const outPct = gross ? 100 - inPct : 0;
                            const isUnspec = (r.mode || "").toLowerCase() === "unspecified";
                            return (
                                <li key={r.mode}>
                                    <button
                                        type="button"
                                        onClick={() => openDrill(r.mode)}
                                        data-testid={`pm-mode-${r.mode.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="group flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-1 text-left transition hover:bg-slate-50"
                                    >
                                        <div className={`w-40 shrink-0 truncate text-sm font-semibold ${isUnspec ? "text-amber-800" : "text-slate-800"}`} title={r.mode}>
                                            {r.mode}{isUnspec && " ⚠"}
                                        </div>
                                        <div className="relative flex h-7 flex-1 overflow-hidden rounded-md bg-slate-100">
                                            {r.amount_in > 0 && (
                                                <div className="flex items-center justify-end pr-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-[10px] font-bold text-white"
                                                     style={{ width: `${inPct}%`, minWidth: r.amount_in > 0 ? "18px" : 0 }}
                                                     title={`Received ${fmt2(r.amount_in)}`}>
                                                    +{fmt(r.amount_in)}
                                                </div>
                                            )}
                                            {r.amount_out > 0 && (
                                                <div className="flex items-center justify-end pr-1 bg-gradient-to-r from-rose-500 to-red-600 text-[10px] font-bold text-white"
                                                     style={{ width: `${outPct}%`, minWidth: r.amount_out > 0 ? "18px" : 0 }}
                                                     title={`Paid ${fmt2(r.amount_out)}`}>
                                                    −{fmt(r.amount_out)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="w-44 shrink-0 text-right">
                                            <div className="font-mono text-xs">
                                                <span className="text-emerald-700 font-semibold">+{fmt(r.amount_in)}</span>
                                                <span className="mx-1 text-slate-300">/</span>
                                                <span className="text-rose-700 font-semibold">−{fmt(r.amount_out)}</span>
                                            </div>
                                            <div className="text-[10px] text-slate-500">{r.count_in} in · {r.count_out} out · {r.unique_parties} part{r.unique_parties === 1 ? "y" : "ies"}</div>
                                        </div>
                                        <CaretRight size={14} className="text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-slate-700" />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                    <div className="font-display text-sm font-semibold text-slate-900">Detailed Breakdown</div>
                    <div className="text-[11px] text-slate-500">{rows.length} mode{rows.length === 1 ? "" : "s"}</div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm" data-testid="pm-table">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-4 py-2 text-left">Payment Mode</th>
                                <th className="px-3 py-2 text-right">In</th>
                                <th className="px-3 py-2 text-right text-emerald-700">Received (₹)</th>
                                <th className="px-3 py-2 text-right">Out</th>
                                <th className="px-3 py-2 text-right text-rose-700">Paid (₹)</th>
                                <th className="px-3 py-2 text-right">Net (₹)</th>
                                <th className="px-3 py-2 text-right">Parties</th>
                                <th className="px-3 py-2 text-right">Share</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredRows.map((r) => (
                                <tr key={r.mode} onClick={() => openDrill(r.mode)}
                                    className="cursor-pointer hover:bg-slate-50"
                                    data-testid={`pm-table-row-${r.mode.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <td className="px-4 py-2 font-medium text-slate-900">{r.mode}</td>
                                    <td className="px-3 py-2 text-right font-mono text-slate-600">{r.count_in}</td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-emerald-700">+{fmt2(r.amount_in)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-slate-600">{r.count_out}</td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-rose-700">−{fmt2(r.amount_out)}</td>
                                    <td className={`px-3 py-2 text-right font-mono font-bold tabular-nums ${Number(r.net) < 0 ? "text-rose-700" : "text-slate-900"}`}>{fmt2(r.net)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-slate-600">{r.unique_parties}</td>
                                    <td className="px-3 py-2 text-right font-mono text-slate-600">{r.share_pct}%</td>
                                </tr>
                            ))}
                            {rows.length > 0 && (
                                <tr className="bg-amber-50/40 font-bold">
                                    <td className="px-4 py-2 uppercase tracking-wider text-[10px] text-amber-900">Total</td>
                                    <td className="px-3 py-2 text-right font-mono text-amber-900">—</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-800">+{fmt2(totals.amount_in)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-amber-900">—</td>
                                    <td className="px-3 py-2 text-right font-mono tabular-nums text-rose-800">−{fmt2(totals.amount_out)}</td>
                                    <td className={`px-3 py-2 text-right font-mono tabular-nums ${Number(totals.net) < 0 ? "text-rose-800" : "text-amber-900"}`}>{fmt2(totals.net)}</td>
                                    <td className="px-3 py-2 text-right text-amber-900">—</td>
                                    <td className="px-3 py-2 text-right font-mono text-amber-900">100%</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

function Kpi({ label, value, icon: Icon, tone = "slate", accent, testId }) {
    const toneCls = {
        emerald: "from-emerald-500 to-teal-600",
        amber: "from-amber-500 to-orange-500",
        blue: "from-blue-500 to-indigo-600",
    }[tone] || "from-slate-500 to-slate-700";
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testId}>
            <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-500">{label}</div>
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${toneCls} text-white shadow-sm`}>
                    <Icon size={16} weight="bold" />
                </div>
            </div>
            <div className={`mt-3 font-display text-2xl font-bold tabular-nums ${accent ? "text-emerald-800" : "text-slate-900"}`}>
                {value}
            </div>
        </div>
    );
}
